import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CoreModule, LocalizationModule } from '@abp/ng.core';
import { SharedModule } from '@digiwall/goliath-core';
import { conditionalValidator, DwInputRequiredDirective } from '@digiwall/core';
import {
  BillingMethod,
  CreateUpdateCustomerSubscriptionDto,
  CustomerInvoiceDto,
  CustomerSubscriptionService,
  DayBookDto,
  DayBookService,
} from '@digiwall/goliath-documents/proxy';
import { GoliathDocumentsEnumerationTypeStore } from './../../../../enumeration-types/enumeration-type.store';
import { DocRole } from '@digiwall/goliath-core/proxy';
import { GoliathProductsEnumerationTypeStore } from '@digiwall/goliath-products';
import { DwButtonDirective } from '@digiwall/ui-kit';
@Component({
  selector: 'subscription-creation-dialog-box',
  standalone: true,
  templateUrl: './subscription-creation-dialog-box.component.html',
  imports: [
    CommonModule,
    CoreModule,
    SharedModule,
    CommonModule,
    SharedModule,
    ReactiveFormsModule,
    DwInputRequiredDirective,
    LocalizationModule,
    DwButtonDirective,
  ],
})
export class SubscriptionCreationDialogBoxComponent implements OnInit {
  invoice? = signal<CustomerInvoiceDto>(null);
  lineIds? = signal<string[]>(null);
  protected form: FormGroup;
  protected readonly isSaving = signal(false);

  enumerationTypeStore = inject(GoliathDocumentsEnumerationTypeStore);
  productEnumerationTypeStore = inject(GoliathProductsEnumerationTypeStore);
  dayBooks: DayBookDto[] = [];
  BillingMethods = BillingMethod;

  constructor(
    private formBuilder: FormBuilder,
    private dayBookService: DayBookService,
    private subscriptionService: CustomerSubscriptionService,
    protected modal: NgbActiveModal,
  ) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      status: [null, Validators.required],
      dayBookId: [null, Validators.required],
      frequency: [null, Validators.required],
      kind: [null, Validators.required],
      billingMethod: [null, Validators.required],
      billingMode: [
        null,
        conditionalValidator(
          () => this.form?.get('billingMethod')?.value == BillingMethod.Auto,
          Validators.required,
          () => [this.form!.get('billingMethod')!.valueChanges],
        ),
      ],
      startDate: [new Date().toISOString(), Validators.required],
      endDate: [null],
      reference: [this.invoice()?.reference],
    });
    this.load();
    this.setDefaultValue();
  }

  load() {
    this.dayBookService.getDayBooksByRole(DocRole.CustomerSubscription).subscribeOnce(res => {
      this.dayBooks = res;
      this.form.get('dayBookId').setValue(this.dayBooks[0]?.id);
    });
  }

  private setDefaultValue(): void {
    this.enumerationTypeStore.ensureLoaded().subscribeOnce(() => {
      this.form
        .get('status')
        .setValue(
          this.enumerationTypeStore.subscriptionStatus().find(x => x.isDefault)?.id ||
            this.enumerationTypeStore.subscriptionStatus()[0]?.id,
        );
      this.form
        .get('frequency')
        .setValue(this.productEnumerationTypeStore.frequencies().find(x => x.isDefault)?.id);
      this.form
        .get('kind')
        .setValue(
          this.enumerationTypeStore.subscriptionKinds().find(x => x.isDefault)?.id ||
            this.enumerationTypeStore.subscriptionKinds()[0]?.id,
        );
      this.form
        .get('billingMode')
        .setValue(this.enumerationTypeStore.billingModes().find(x => x.isDefault)?.id);
      this.form
        .get('billingMethod')
        .setValue(this.enumerationTypeStore.billingMethods().find(x => x.isDefault)?.id);
    });
  }

  save() {
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    let creationDto: CreateUpdateCustomerSubscriptionDto = Object.assign({}, this.form.value);
    creationDto.lineIds = this.lineIds();

    this.isSaving.set(true);
    this.subscriptionService
      .createFromCustomerInvoice(this.invoice()?.id, creationDto)
      .subscribeOnce(
        response => {
          this.isSaving.set(false);
          this.modal.close(response.id);
        },
        () => {
          this.isSaving.set(false);
        },
      );
  }
}
